/**
 * The authorisation vocabulary, shared by the API and the React client.
 *
 * The backend seeds and enforces these; the frontend uses them only to decide
 * what to render. Hiding a control is a usability choice, never a security
 * boundary — every protected route re-checks on the server.
 *
 * Codes are immutable. Renaming one silently revokes access for everybody who
 * holds it, so a rename is a migration, not an edit.
 */

/** Every permission in `docs/RBAC_MATRIX.md`, grouped by the module that owns it. */
export const PERMISSIONS = {
  AUTH_SESSIONS_MANAGE: 'auth.sessions.manage',

  USERS_CREATE: 'users.create',
  USERS_READ: 'users.read',
  USERS_UPDATE: 'users.update',
  USERS_DEACTIVATE: 'users.deactivate',

  ROLES_MANAGE: 'roles.manage',

  CUSTOMERS_CREATE: 'customers.create',
  CUSTOMERS_READ: 'customers.read',
  CUSTOMERS_UPDATE: 'customers.update',
  CUSTOMERS_EXPORT: 'customers.export',

  KYC_REVIEW: 'kyc.review',

  CUSTOMER_DOCUMENTS_READ: 'customer_documents.read',
  CUSTOMER_DOCUMENTS_DOWNLOAD: 'customer_documents.download',
  CUSTOMER_DOCUMENTS_REVIEW: 'customer_documents.review',
  CUSTOMER_DOCUMENTS_CREATE_OWN: 'customer_documents.create_own',
  CUSTOMER_DOCUMENTS_READ_OWN: 'customer_documents.read_own',

  BANK_ACCOUNTS_CREATE: 'bank_accounts.create',
  BANK_ACCOUNTS_READ_MASKED: 'bank_accounts.read_masked',
  BANK_ACCOUNTS_MANAGE: 'bank_accounts.manage',

  PLANS_MANAGE: 'plans.manage',

  SUBSCRIPTIONS_CREATE: 'subscriptions.create',
  SUBSCRIPTIONS_READ: 'subscriptions.read',
  SUBSCRIPTIONS_UPDATE: 'subscriptions.update',
  SUBSCRIPTIONS_APPROVE: 'subscriptions.approve',
  SUBSCRIPTIONS_CANCEL: 'subscriptions.cancel',

  MANDATES_CREATE: 'mandates.create',
  MANDATES_READ: 'mandates.read',
  MANDATES_CHECK_STATUS: 'mandates.check_status',
  MANDATES_REQUEST_CANCEL: 'mandates.request_cancel',
  MANDATES_CANCEL_MANAGE: 'mandates.cancel_manage',

  PROVIDER_BANKS_READ: 'provider_banks.read',
  PROVIDER_BANKS_REFRESH: 'provider_banks.refresh',

  BILLING_READ: 'billing.read',
  BILLING_READ_OWN: 'billing.read_own',
  BILLING_GENERATE: 'billing.generate',
  BILLING_ADJUST: 'billing.adjust',
  BILLING_MANAGE: 'billing.manage',
  BILLING_REMINDERS_SEND: 'billing.reminders.send',

  PAYMENTS_READ: 'payments.read',
  PAYMENTS_READ_OWN: 'payments.read_own',
  PAYMENT_ATTEMPTS_READ: 'payment_attempts.read',
  RECEIPTS_READ: 'receipts.read',
  RECEIPTS_READ_OWN: 'receipts.read_own',

  PAYMENTS_AUTHORIZE_OWN: 'payments.authorize_own',
  PAYMENTS_RETRY: 'payments.retry',
  PAYMENTS_RECORD_MANUAL: 'payments.record_manual',
  PAYMENTS_APPROVE_MANUAL: 'payments.approve_manual',
  PAYMENTS_RECONCILE: 'payments.reconcile',
  PAYMENTS_WAIVE: 'payments.waive',

  REFUNDS_REQUEST: 'refunds.request',
  REFUNDS_APPROVE: 'refunds.approve',

  NOTIFICATIONS_SEND: 'notifications.send',
  NOTIFICATIONS_TEMPLATES_MANAGE: 'notifications.templates.manage',
  NOTIFICATIONS_READ: 'notifications.read',
  NOTIFICATIONS_RETRY: 'notifications.retry',

  REPORTS_READ: 'reports.read',
  REPORTS_EXPORT: 'reports.export',

  SYSTEM_HEALTH_READ: 'system_health.read',
  JOBS_READ: 'jobs.read',

  AUDIT_READ: 'audit.read',

  SETTINGS_MANAGE: 'settings.manage',
  INTEGRATIONS_MANAGE: 'integrations.manage',
} as const;

/** Union of every permission code. */
export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Every permission code as a flat, iteration-friendly list. */
export const ALL_PERMISSION_CODES = Object.values(PERMISSIONS) as readonly PermissionCode[];

/** Role codes from `docs/RBAC_MATRIX.md`. */
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  SYSTEM_ADMIN: 'SYSTEM_ADMIN',
  FINANCE_MANAGER: 'FINANCE_MANAGER',
  FINANCE_OFFICER: 'FINANCE_OFFICER',
  SALES_ONBOARDING: 'SALES_ONBOARDING',
  CUSTOMER_SUPPORT: 'CUSTOMER_SUPPORT',
  OPERATIONS_INSTALLATION: 'OPERATIONS_INSTALLATION',
  AUDITOR_COMPLIANCE: 'AUDITOR_COMPLIANCE',
  CUSTOMER: 'CUSTOMER',
} as const;

export type RoleCode = (typeof ROLES)[keyof typeof ROLES];

export const ALL_ROLE_CODES = Object.values(ROLES) as readonly RoleCode[];
